import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { consumePkce } from "@/lib/pkce";
import { authorize, setAppId } from "@/lib/deriv";
import { useConfig } from "@/hooks/useConfig";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import Logo from "@/components/marketing/Logo";

// Deriv may redirect back in TWO formats depending on the OAuth flow used:
// 1) Legacy implicit: #token1=...&acct1=...&cur1=...
// 2) New PKCE: ?code=...&state=...
const parseTokensFromHash = (hash) => {
  if (!hash) return [];
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const accounts = [];
  let i = 1;
  while (params.get(`token${i}`)) {
    accounts.push({
      token: params.get(`token${i}`),
      account_id: params.get(`acct${i}`),
      currency: params.get(`cur${i}`),
    });
    i++;
  }
  return accounts;
};

const parseCodeFromQuery = (search) => {
  if (!search) return null;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error") || params.get("error_description");
  if (error) return { error };
  if (code) return { code, state };
  return null;
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { config } = useConfig();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("Securing your session…");
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    if (!config) return;
    handled.current = true;

    (async () => {
      setAppId(config.deriv_app_id);

      // Try legacy implicit flow first (hash fragment)
      const implicitAccounts = parseTokensFromHash(window.location.hash);
      if (implicitAccounts.length) {
        const primary = implicitAccounts[0];
        login({
          token: primary.token,
          account_id: primary.account_id,
          currency: primary.currency,
          accounts: implicitAccounts,
        });
        api
          .post("/track/login", {
            account_id: primary.account_id,
            currency: primary.currency,
            user_agent: navigator.userAgent,
          })
          .catch(() => {});
        setStatus("success");
        setMessage(`Connected as ${primary.account_id}`);
        setTimeout(() => navigate("/trade"), 800);
        return;
      }

      // Try new PKCE flow (query param ?code=...)
      const pkceResult = parseCodeFromQuery(window.location.search);
      if (pkceResult?.error) {
        setStatus("error");
        setMessage(`Deriv rejected the login: ${pkceResult.error}`);
        // v8.9.127 — stay on this page so the user sees what happened.
        // Previously we auto-navigated to "/" which made the error
        // invisible and looked like "the login just doesn't work".
        return;
      }
      if (pkceResult?.code) {
        const stored = consumePkce();
        if (!stored?.verifier) {
          setStatus("error");
          setMessage("PKCE verifier missing (browser may have cleared storage during the redirect). Please try logging in again — a one-tap retry below should fix this.");
          return;
        }
        setMessage("Exchanging authorization with Deriv…");
        try {
          const res = await api.post("/auth/exchange-code", {
            code: pkceResult.code,
            code_verifier: stored.verifier,
            redirect_uri: `${window.location.origin.replace("://www.", "://")}/auth/callback`,
          });
          const accessToken = res.data?.access_token;
          if (!accessToken) {
            const detail = res.data?.detail || res.data?.error || "No access token returned by Deriv";
            throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
          }

          // Resolve the user's primary Deriv account via the new REST API.
          // This proxy adds Deriv-App-ID so the OTP and all subsequent trades
          // are attributed to our partner app → markup credits accrue.
          let accountId = "OAUTH_USER";
          let currency = "USD";
          let accounts = [];
          try {
            setMessage("Fetching your Deriv accounts…");
            const acctsRes = await api.get("/deriv/accounts", {
              headers: { Authorization: `Bearer ${accessToken}` },
              validateStatus: () => true,
            });
            const payload = acctsRes.data?.data || acctsRes.data || {};
            const list = payload.accounts || payload || [];
            const arr = Array.isArray(list) ? list : [];
            if (arr.length > 0) {
              // Score accounts so we always pick a REAL one if any exist.
              // Deriv login IDs starting with VRTC = virtual, everything
              // else = real. Also honour explicit account_type/is_virtual
              // flags. Higher score = better candidate.
              const scoreAccount = (a) => {
                const id = a.id || a.account_id || a.loginid || "";
                const explicitVirtual =
                  a.is_virtual === true || a.virtual === true ||
                  a.account_type === "virtual" || a.account_type === "demo" ||
                  a.type === "virtual" || a.type === "demo";
                const explicitReal =
                  a.is_virtual === false || a.virtual === false ||
                  a.account_type === "real" || a.type === "real";
                if (explicitVirtual) return 0;   // explicit wins
                if (explicitReal) return 100;
                // Deriv login-id prefixes: VRTC* / DOT* = demo, ROT* / CR* / MF* = real
                if (/^(VRTC|DOT)/i.test(id)) return 0;
                return 50; // unknown — treat as real-ish
              };
              const sorted = [...arr].sort((x, y) => scoreAccount(y) - scoreAccount(x));
              const best = sorted[0];
              accountId = best.id || best.account_id || best.loginid || accountId;
              currency = best.currency || currency;
              accounts = arr.map((a) => {
                const id = a.id || a.account_id || a.loginid;
                const isVirtual =
                  a.is_virtual === true || a.virtual === true ||
                  a.account_type === "virtual" || a.account_type === "demo" ||
                  a.type === "virtual" || a.type === "demo" ||
                  /^(VRTC|DOT)/i.test(id || "");
                return {
                  token: accessToken,
                  account_id: id,
                  currency: a.currency,
                  balance: a.balance,
                  is_virtual: isVirtual,
                  account_type: a.account_type || a.type ||
                    (isVirtual ? "virtual" : "real"),
                };
              });
            }
          } catch (apiErr) {
            console.warn("[VTS] Could not fetch accounts via new REST:", apiErr);
          }
          if (!accounts.length) {
            accounts = [{ token: accessToken, account_id: accountId, currency }];
          }
          const session = {
            token: accessToken,
            account_id: accountId,
            currency,
            accounts,
            connection_type: "otp",
            markup_ready: true,
          };
          login(session);
          api
            .post("/track/login", {
              account_id: accountId,
              currency,
              user_agent: navigator.userAgent,
            })
            .catch(() => {});
          setStatus("success");
          setMessage(`Connected as ${accountId}`);
          setTimeout(() => navigate("/trade"), 900);
        } catch (e) {
          setStatus("error");
          const errBody = e?.response?.data;
          const detail = errBody?.detail || errBody?.error || e?.message || "Unknown error";
          setMessage(`Login failed: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
          // v8.9.127 — stay so user can read the actual Deriv error.
        }
        return;
      }

      // No tokens, no code, no error — bad callback
      setStatus("error");
      setMessage("No login data found in callback. Please tap 'Try Again' below.");
    })();
  }, [navigate, login, config]);

  return (
    <div
      data-testid="auth-callback-page"
      className="min-h-screen bg-vts-bg flex items-center justify-center p-6"
    >
      <div className="vts-card p-10 max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <Logo size="lg" showText={false} />
        </div>
        <h1 className="font-heading font-bold text-2xl text-white mb-3 tracking-tight">
          {status === "processing" && "Connecting to Deriv…"}
          {status === "success" && "Welcome aboard"}
          {status === "error" && "Connection failed"}
        </h1>
        <div className="flex items-center justify-center gap-2 text-vts-silver">
          {status === "processing" && <Loader2 className="h-4 w-4 animate-spin text-vts-blue" />}
          {status === "success" && <CheckCircle2 className="h-4 w-4 text-vts-profit" />}
          {status === "error" && <AlertCircle className="h-4 w-4 text-vts-loss" />}
          <span data-testid="auth-status-message" className="text-sm font-mono">
            {message}
          </span>
        </div>
        {status === "error" && (
          <div className="mt-6 flex flex-col gap-2">
            <button
              data-testid="auth-retry-btn"
              onClick={() => navigate("/trade?connect=oauth")}
              className="rounded-md bg-vts-blue text-white text-sm py-2.5 px-5 font-medium hover:bg-vts-blue/90"
            >
              Try Again
            </button>
            <button
              data-testid="auth-home-btn"
              onClick={() => navigate("/")}
              className="rounded-md bg-vts-surface border border-vts-line text-vts-silver text-sm py-2 px-5 hover:text-white"
            >
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
